import React, {useCallback} from 'react';
import {useSelector, shallowEqual, useDispatch} from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {getAllScots, getEngagementAllScots} from '../../../../../../actions/scots/scotsactions';
import {getAllItApplications} from '../../../../../../actions/ITApplication/itapplicationaction';
import {getAllItProcesses} from '../../../../../../actions/ITProcess/itprocessactions';
import {useDocumentId, useEngagementId, useDocumentEngagementId} from '../../../../../../util/customHooks';
import {getClientFiltersForBodyType} from './Common/clientFilters.utils';
import {labels} from '../../../../../../util/utils';
import {entities, formBodyType, itApplicationFilter, controlType} from '../../../../../../util/uiconstants';
import Fetch from '../../../../Fetch/fetch';
import NoDataPlaceholder from '../../../../ResourceWithLink/NoDataPlaceholder';
import BodySpecialPagination from './BodySpecialPagination';
import NoResultsPlaceholder from '../../../../NoResultsPlaceholder/NoResultsPlaceholder';

const common = {
	includePagination: true
};

const actions = {
	[formBodyType.ISA315ScotItApplication]: (engagementId, bodyId, bodyTypeId, filters, dispatch) =>
		dispatch(
			getAllScots(
				engagementId,
				{
					bodyId,
					bodyTypeId,
					entityId: entities.SCOT,
					clientFilters: {...getClientFiltersForBodyType(formBodyType.ISA315ScotItApplication)}
				},
				{
					...filters,
					includeControl: true,
					includeIT: true,
					filterControlType: controlType.itApplicationControl + ',' + controlType.itDependentManualControl,
					...common
				}
			)
		),
	[formBodyType.ISA315ItApplicationScot]: (engagementId, bodyId, bodyTypeId, filters, dispatch) => {
		dispatch(
			getAllItApplications(
				engagementId,
				{
					bodyId,
					bodyTypeId,
					entityId: entities.ItSoApplication
				},
				{
					...filters,
					includeControl: true,
					includeScot: true,
					filterITApplicationType: itApplicationFilter.itApplication,
					...common
				}
			)
		);
	},
	[formBodyType.ISA315ItProcessTask]: (engagementId, bodyId, bodyTypeId, filters, dispatch) =>
		dispatch(
			getAllItProcesses(
				engagementId,
				{
					bodyId,
					bodyTypeId,
					entityId: entities.ItProcess
				},
				{
					...filters,
					IncludeRelatedTask: true,
					...common
				}
			)
		),
	[formBodyType.ISA315ItProcessItRisk]: (engagementId, bodyId, bodyTypeId, filters, dispatch) =>
		dispatch(
			getAllItProcesses(
				engagementId,
				{
					bodyId,
					bodyTypeId,
					entityId: entities.ItProcess,
					clientFilters: {...getClientFiltersForBodyType(formBodyType.ISA315ItProcessItRisk)}
				},
				{
					...filters,
					IncludeITApp: true,
					IncludeControl: true,
					IncludeITRisk: true,
					IncludeITControl: true,
					...common
				}
			)
		),
	[formBodyType.ISA315ItGCTestStrategy]: (engagementId, bodyId, bodyTypeId, filters, dispatch) =>
		dispatch(
			getAllItProcesses(
				engagementId,
				{
					bodyId,
					bodyTypeId,
					entityId: entities.ItProcess,
					clientFilters: {...getClientFiltersForBodyType(formBodyType.ISA315ItGCTestStrategy)}
				},
				{
					...filters,
					IncludeITApp: true,
					IncludeControl: true,
					IncludeITControl: true,
					IncludeITRisk: true,
					...common
				}
			)
		),
	[formBodyType.ISA315SoScot]: (engagementId, bodyId, bodyTypeId, filters, dispatch) =>
		dispatch(
			getAllItApplications(
				engagementId,
				{
					bodyId,
					bodyTypeId,
					entityId: entities.ItSoApplication
				},
				{
					...filters,
					includeControl: true,
					includeSCOT: true,
					filterITApplicationType: itApplicationFilter.so,
					...common
				}
			)
		),
	[formBodyType.ISA315ManageItSp]: (engagementId, bodyId, bodyTypeId, filters, dispatch) =>
		dispatch(
			getAllItProcesses(
				engagementId,
				{
					bodyId,
					bodyTypeId,
					entityId: entities.ItProcess
				},
				{
					...filters,
					includeITControl: true,
					includeITRisk: true,
					...common
				}
			)
		)
};

function BodySpecialFetch({
	bodyId,
	bodyTypeId,
	filters,
	noDataPlaceholder,
	noResultsPlaceholder,
	onFilterChange,
	children,
	isTargetEngagement,
	forceUpdate,
	forceUpdateCallback
}) {
	const dispatch = useDispatch();

	const documentEngagementId = useDocumentEngagementId(),
		engagementId = useEngagementId(),
		documentId = useDocumentId();

	const fetchCallback = useCallback(
		() =>
			actions[bodyTypeId](
				engagementId,
				bodyId,
				bodyTypeId,
				{
					...filters
				},
				dispatch
			),
		[dispatch, engagementId, bodyId, bodyTypeId, filters, forceUpdate]
	);

	const {allIds, totalEntityCount} = useSelector((state) => state.bodiesSpecial.metadata[bodyId] || {}, shallowEqual);

	let placeholder = null;
	if (allIds && allIds.length === 0) {
		if (filters.searchString) {
			placeholder = <NoResultsPlaceholder placeholder={noResultsPlaceholder} />;
		} else if (filters.searchString === undefined) {
			placeholder = (
				<StyledNoDataPlaceholder className="StyledNoDataPlaceholder">
					<NoDataPlaceholder
						className="isa315-body-special-no-data-placeholder"
						placeholder={noDataPlaceholder.text}
						onPlaceholderClickConfig={noDataPlaceholder.onClickConfig}
					/>
				</StyledNoDataPlaceholder>
			);
		}
	}

	const pagination = allIds && totalEntityCount > filters.pagingOptions[0];
	let minHeight = 25;
	if (pagination) {
		minHeight = 25;
	}

	return (
		<StyledBodySpecialFetch className="StyledBodySpecialFetch" minHeight={minHeight}>
			<Fetch fetch={fetchCallback}>
				{allIds && allIds.length > 0
					? React.Children.map(children, (child) =>
							React.cloneElement(child, {
								allIds,
								bodyId,
								documentEngagementId: Number(documentEngagementId),
								engagementId: Number(engagementId),
								documentId,
								isTargetEngagement,
								bodyTypeId,
								forceUpdate,
								forceUpdateCallback
							})
					  )
					: placeholder}
			</Fetch>
			{pagination && <BodySpecialPagination bodyId={bodyId} filters={filters} onFilterChange={onFilterChange} />}
		</StyledBodySpecialFetch>
	);
}

BodySpecialFetch.propTypes = {
	bodyId: PropTypes.number.isRequired,
	bodyTypeId: PropTypes.number.isRequired,
	filters: PropTypes.instanceOf(Object),
	noDataPlaceholder: PropTypes.string.isRequired,
	noResultsPlaceholder: PropTypes.string,
	onFilterChange: PropTypes.func.isRequired,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired
};

BodySpecialFetch.defaultProps = {
	noResultsPlaceholder: labels.noResultsFound
};

export default BodySpecialFetch;

const StyledBodySpecialFetch = styled.section`
	height: 100%;
	min-height: ${(props) => props.minHeight}rem;
`;
const StyledNoDataPlaceholder = styled.section`
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: inherit;
	max-height: inherit;
	align-content: center;
	color: var(--neutrals-700);
	span {
		font-size: var(--px-16);
		text-align: center;
		a,
		.createClickableTextLink {
			font-weight: bold !important;
			cursor: pointer;
			font-size: var(--px-14);
			color: var(--blue-600);
			display: inline-flex;
			margin-left: var(--px-5);
		}
	}
	a,
	.createClickableTextLink {
		font-weight: bold !important;
		cursor: pointer;
		font-size: var(--px-14);
		color: var(--blue-600);
		display: inline-flex;
		margin-left: var(--px-5);
	}
	.isa315-body-special-no-data-placeholder {
		height: calc(100% - var(--px-60));
		display: flex !important;
		flex-direction: column !important;
		justify-content: center;
		align-items: center;
		.createRichTextElementSpanWrapper {
			br {
				display: none;
			}
		}
	}
`;
